"use client";

import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import ky from "ky";
import _ from "lodash";
import moment from "moment-timezone";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SpotlightCard from "@/components/ui/spotlight-card";
import { ClientStrapiResultDocument } from "@/types/v2/ClientStrapi";
import { EmployeeStrapiResultDocument } from "@/types/v2/EmployeeStrapi";
import { PeriodStrapiResultDocument } from "@/types/v2/PeriodStrapi";
import { SMFStrapiResultDocument } from "@/types/v2/SMFStrapi";
import { SMGStrapiResultDocument } from "@/types/v2/SMGStrapi";
import { UMAStrapiResultDocument } from "@/types/v2/UMAStrapi";
import { UMIStrapiResultDocument } from "@/types/v2/UMIStrapi";
import { cn } from "@/utils";

const toTitleCase = (str: string): string => {
  return str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
};

export default function Page() {
  const [greeting, setGreeting] = useState("Bienvenido");
  const router = useRouter();

  const { localData, flags } = useModule();

  useEffect(() => {
    const now = new Date();
    const hours = now.getHours();
    if (hours >= 5 && hours < 12) {
      setGreeting("Buenos días");
    } else if (hours >= 12 && hours < 18) {
      setGreeting("Buenas tardes");
    } else {
      setGreeting("Buenas noches");
    }
  }, []);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-6 lg:p-8 space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <p className="text-sm text-muted-foreground">
          {moment().format("dddd, D [de] MMMM [de] YYYY")}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">
          {greeting},{" "}
          <span className="text-primary">
            {localData.me ? toTitleCase(localData.me) : "Usuario"}
          </span>
        </h1>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        variants={itemVariants}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {/* Empresas */}
        <SpotlightCard
          className="cursor-pointer p-5"
          spotlightColor="rgba(20, 184, 166, 0.15)"
          onClick={() => router.push("/empresas")}
        >
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Empresas</p>
              <p className="text-3xl font-semibold">
                {flags.isLoading ? (
                  <Icon
                    icon="solar:refresh-linear"
                    className="w-6 h-6 animate-spin text-muted-foreground"
                  />
                ) : (
                  localData?.clients?.length ?? 0
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                Registradas en el sistema
              </p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon
                icon="solar:buildings-2-linear"
                className="w-5 h-5 text-primary"
              />
            </div>
          </div>
        </SpotlightCard>

        {/* Colaboradores */}
        <SpotlightCard
          className="cursor-pointer p-5"
          spotlightColor="rgba(20, 184, 166, 0.15)"
          onClick={() => router.push("/v2/employees")}
        >
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Colaboradores</p>
              <p className="text-3xl font-semibold">
                {flags.isLoading ? (
                  <Icon
                    icon="solar:refresh-linear"
                    className="w-6 h-6 animate-spin text-muted-foreground"
                  />
                ) : (
                  localData?.employees?.length ?? 0
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                Activos actualmente
              </p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon
                icon="solar:users-group-rounded-linear"
                className="w-5 h-5 text-primary"
              />
            </div>
          </div>
        </SpotlightCard>

        {/* Períodos */}
        <SpotlightCard
          className="cursor-pointer p-5"
          spotlightColor="rgba(20, 184, 166, 0.15)"
          onClick={() => router.push("/v2/periods")}
        >
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Períodos</p>
              <p className="text-3xl font-semibold">
                {flags.isLoadingPeriods ? (
                  <Icon
                    icon="solar:refresh-linear"
                    className="w-6 h-6 animate-spin text-muted-foreground"
                  />
                ) : (
                  localData?.periods?.length ?? 0
                )}
              </p>
              <p className="text-xs text-muted-foreground">Activos</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon
                icon="solar:calendar-linear"
                className="w-5 h-5 text-primary"
              />
            </div>
          </div>
        </SpotlightCard>

        {/* Próximos Pagos */}
        <SpotlightCard
          className="cursor-pointer p-5"
          spotlightColor="rgba(245, 158, 11, 0.15)"
          onClick={() => router.push("/v2/periods")}
        >
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Próximos Pagos</p>
              <p className="text-3xl font-semibold">
                {flags.isLoadingPeriods ? (
                  <Icon
                    icon="solar:refresh-linear"
                    className="w-6 h-6 animate-spin text-muted-foreground"
                  />
                ) : (
                  Object.keys(localData?.groupedPeriods ?? {}).length
                )}
              </p>
              <p className="text-xs text-muted-foreground">Este mes</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <Icon
                icon="solar:clock-circle-linear"
                className="w-5 h-5 text-warning"
              />
            </div>
          </div>
        </SpotlightCard>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Payments - Takes 2 columns */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <SpotlightCard
            className="p-0"
            spotlightColor="rgba(20, 184, 166, 0.15)"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon
                    icon="solar:calendar-mark-linear"
                    className="w-5 h-5 text-muted-foreground"
                  />
                  <h2 className="font-medium">Próximos Pagos</h2>
                  <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                    Mes actual
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => router.push("/v2/periods")}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Ver todos
                  <Icon icon="solar:arrow-right-linear" className="w-4 h-4 ml-1" />
                </Button>
              </div>

              <div className="p-4">
                {flags.isLoadingPeriods ? (
                  <div className="flex items-center justify-center py-8">
                    <Icon
                      icon="solar:refresh-linear"
                      className="w-5 h-5 text-muted-foreground animate-spin"
                    />
                  </div>
                ) : _.isEmpty(localData?.groupedPeriods) ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Icon
                      icon="solar:calendar-minimalistic-linear"
                      className="w-10 h-10 text-muted-foreground/50 mb-2"
                    />
                    <p className="text-sm text-muted-foreground">
                      No hay pagos programados este mes
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {_.map(localData?.groupedPeriods, (periods, key) => {
                      const period = periods[0];
                      const payday = moment(period?.attributes.payday);
                      const isToday = payday.isSame(moment(), "day");
                      const isPast = payday.isBefore(moment(), "day");

                      return (
                        <div
                          key={key}
                          onClick={() =>
                            router.push(
                              `/v2/periods/${period?.attributes.payroll?.data?.id}/${period?.id}/prenomina`
                            )
                          }
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg cursor-pointer",
                            "border border-transparent",
                            "hover:bg-muted/50 hover:border-border",
                            "transition-colors"
                          )}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className={cn(
                                "h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0",
                                isToday
                                  ? "bg-warning/10"
                                  : isPast
                                    ? "bg-destructive/10"
                                    : "bg-muted"
                              )}
                            >
                              <Icon
                                icon="solar:calendar-linear"
                                className={cn(
                                  "w-4 h-4",
                                  isToday
                                    ? "text-warning"
                                    : isPast
                                      ? "text-destructive"
                                      : "text-muted-foreground"
                                )}
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">
                                {period?.attributes.name}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {period?.attributes.client?.data?.attributes
                                  ?.businessName || "Sin empresa"}{" "}
                                · {key}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span
                              className={cn(
                                "text-sm",
                                isToday
                                  ? "text-warning font-medium"
                                  : isPast
                                    ? "text-destructive"
                                    : "text-muted-foreground"
                              )}
                            >
                              {isToday ? "Hoy" : payday.format("DD MMM")}
                            </span>
                            <Icon
                              icon="solar:alt-arrow-right-linear"
                              className="w-4 h-4 text-muted-foreground"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
          </SpotlightCard>
        </motion.div>

        {/* Factors - Takes 1 column */}
        <motion.div variants={itemVariants}>
          <SpotlightCard
            className="p-0"
            spotlightColor="rgba(20, 184, 166, 0.15)"
          >
            <div className="flex items-center gap-2 p-4 border-b border-border">
                <Icon
                  icon="solar:chart-2-linear"
                  className="w-5 h-5 text-muted-foreground"
                />
                <h2 className="font-medium">Factores de Cálculo</h2>
              </div>

              <div className="p-4 space-y-3">
                <FactorRow
                  title="UMA"
                  subtitle="Unidad de Medida"
                  value={localData.uma?.attributes.value}
                  date={localData.uma?.attributes.effectiveDate}
                  isLoading={flags.isLoadingUMA}
                />
                <FactorRow
                  title="UMI"
                  subtitle="Unidad Mixta Infonavit"
                  value={localData.umi?.attributes.value}
                  date={localData.umi?.attributes.effectiveDate}
                  isLoading={flags.isLoadingUMI}
                />
                <FactorRow
                  title="SMG"
                  subtitle="Salario Mínimo General"
                  value={localData.smg?.attributes.value}
                  date={localData.smg?.attributes.effectiveDate}
                  isLoading={flags.isLoadingSMG}
                />
                <FactorRow
                  title="SMF"
                  subtitle="Salario Mínimo Frontera"
                  value={localData.smf?.attributes.value}
                  date={localData.smf?.attributes.effectiveDate}
                  isLoading={flags.isLoadingSMF}
                />
              </div>
          </SpotlightCard>
        </motion.div>
      </div>
    </motion.div>
  );
}

function FactorRow({
  title,
  subtitle,
  value,
  date,
  isLoading,
}: {
  title: string;
  subtitle: string;
  value: number | undefined;
  date: string | Date | undefined;
  isLoading: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <span className="text-xs font-semibold text-primary">
            {title.charAt(0)}
          </span>
        </div>
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <div className="text-right">
        {isLoading ? (
          <Icon
            icon="solar:refresh-linear"
            className="w-4 h-4 text-muted-foreground animate-spin"
          />
        ) : (
          <>
            <p className="text-sm font-semibold">
              {value !== undefined ? `$${value.toFixed(2)}` : "—"}
            </p>
            <p className="text-xs text-muted-foreground">
              {date ? moment.utc(date).format("DD/MM/YY") : "—"}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const useModule = () => {
  const tenant = Cookies.get("member") || Cookies.get("employee");

  const meQuery = useQuery({
    queryKey: ["me", "dashboard"],
    queryFn: async () => {
      const resp = await ky.get("/api/auth/me").json<any>();
      return resp;
    },
  });

  const clientsQuery = useQuery({
    queryKey: ["clients", "dashboard", tenant],
    queryFn: async () => {
      const resp = await ky
        .post("/api/dashboard/clients", {
          json: {
            tenant,
          },
        })
        .json<{
          clients: ClientStrapiResultDocument[];
          employees: EmployeeStrapiResultDocument[];
        }>();
      return resp;
    },
  });

  const periodsQuery = useQuery({
    queryKey: ["periods", "dashboard", tenant],
    queryFn: async () => {
      const resp = await ky
        .post("/api/dashboard/periods", {
          json: {
            tenant,
          },
        })
        .json<{
          periods: PeriodStrapiResultDocument[];
          groupedPeriods: Record<string, PeriodStrapiResultDocument[]>;
        }>();
      return resp;
    },
  });

  const umaQuery = useQuery({
    queryKey: ["uma", "dashboard"],
    queryFn: async () => {
      const resp = await ky
        .get("/api/dashboard/uma")
        .json<UMAStrapiResultDocument[]>();

      return resp.sort(
        (a, b) =>
          new Date(b.attributes.effectiveDate).getTime() -
          new Date(a.attributes.effectiveDate).getTime()
      )[0];
    },
  });

  const umiQuery = useQuery({
    queryKey: ["umi", "dashboard"],
    queryFn: async () => {
      const resp = await ky
        .get("/api/dashboard/umi")
        .json<UMIStrapiResultDocument[]>();

      return resp.sort(
        (a, b) =>
          new Date(b.attributes.effectiveDate).getTime() -
          new Date(a.attributes.effectiveDate).getTime()
      )[0];
    },
  });

  const smgQuery = useQuery({
    queryKey: ["smg", "dashboard"],
    queryFn: async () => {
      const resp = await ky
        .get("/api/dashboard/smg")
        .json<SMGStrapiResultDocument[]>();

      return resp.sort(
        (a, b) =>
          new Date(b.attributes.effectiveDate).getTime() -
          new Date(a.attributes.effectiveDate).getTime()
      )[0];
    },
  });

  const smfQuery = useQuery({
    queryKey: ["smf", "dashboard"],
    queryFn: async () => {
      const resp = await ky
        .get("/api/dashboard/smf")
        .json<SMFStrapiResultDocument[]>();

      return resp.sort(
        (a, b) =>
          new Date(b.attributes.effectiveDate).getTime() -
          new Date(a.attributes.effectiveDate).getTime()
      )[0];
    },
  });

  const memberId = Cookies.get("member") || "";

  const member = meQuery.data?.members.find(
    (m: any) => m.id === parseInt(memberId, 10)
  );

  return {
    localData: {
      me: member?.name,
      clients: clientsQuery.data?.clients,
      employees: clientsQuery.data?.employees,
      periods: periodsQuery.data?.periods,
      groupedPeriods: periodsQuery.data?.groupedPeriods,
      uma: umaQuery.data,
      umi: umiQuery.data,
      smg: smgQuery.data,
      smf: smfQuery.data,
    },
    flags: {
      isLoading: clientsQuery.isLoading,
      isError: clientsQuery.isError,
      isRefetching: clientsQuery.isRefetching,

      isLoadingPeriods: periodsQuery.isLoading,
      isErrorPeriods: periodsQuery.isError,
      isRefetchingPeriods: periodsQuery.isRefetching,

      isLoadingUMA: umaQuery.isLoading,
      isLoadingUMI: umiQuery.isLoading,
      isLoadingSMG: smgQuery.isLoading,
      isLoadingSMF: smfQuery.isLoading,
    },
  };
};
